/**
 * Desktop: “Viendo” + always “Elegir agendas (n/n)”.
 * Mobile: current agenda + “Cambiar” (no horizontal chip scroll).
 */
import React, { useMemo, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../shared/theme/spacing';
import { agenda, agendaDotColor, agendaFonts } from '../domain/agendaTheme';
import type { AppointmentResource } from '../domain/types';

type Props = {
  isWide: boolean;
  resources: AppointmentResource[];
  desktopSelectedIds: string[];
  mobileResourceId: string | null;
  onMobileResourceChange: (id: string) => void;
  onPressChoose: () => void;
  /** Resumen del día inline (proto mobile: "9 turnos" junto a Viendo). */
  summary?: string;
};

export function AgendaVisibleBar(props: Props) {
  const { resources, isWide } = props;
  const insets = useSafeAreaInsets();
  const [mobilePickerOpen, setMobilePickerOpen] = useState(false);
  const indexById = useMemo(() => new Map(resources.map((r, i) => [r.id, i])), [resources]);

  if (resources.length === 0) return null;

  if (!isWide) {
    const activeId = props.mobileResourceId ?? resources[0]?.id ?? null;
    const active = resources.find((r) => r.id === activeId) ?? resources[0];
    const activeIdx = indexById.get(active.id) ?? 0;
    const activeDot = agendaDotColor(active.color, activeIdx);
    const showSwitcher = resources.length > 1;

    return (
      <>
        <View style={[styles.bar, styles.mobileBar]}>
          <Text style={styles.label}>Viendo</Text>
          <View style={styles.mobileCurrent}>
            <View style={[styles.dot, { backgroundColor: activeDot }]} />
            <Text style={styles.mobileName} numberOfLines={1}>
              {active.displayName}
            </Text>
          </View>
          {props.summary ? (
            <Text style={styles.barSummary} numberOfLines={1}>
              {props.summary}
            </Text>
          ) : null}
          {showSwitcher ? (
            <TouchableOpacity
              style={[styles.changeBtn, Platform.OS === 'web' && styles.webReset]}
              onPress={() => setMobilePickerOpen(true)}
              activeOpacity={0.85}
              accessibilityLabel="Cambiar agenda"
            >
              <Text style={styles.changeBtnText}>Cambiar</Text>
            </TouchableOpacity>
          ) : null}
        </View>

        <Modal
          visible={mobilePickerOpen}
          transparent
          animationType="fade"
          onRequestClose={() => setMobilePickerOpen(false)}
        >
          <Pressable style={styles.sheetBackdrop} onPress={() => setMobilePickerOpen(false)}>
            <Pressable
              style={[styles.sheet, { paddingBottom: Math.max(insets.bottom, spacing.md) }]}
              onPress={(e) => e.stopPropagation()}
            >
              <Text style={styles.sheetTitle}>Elegir agenda</Text>
              <ScrollView style={styles.sheetList}>
                {resources.map((r, i) => {
                  const selected = r.id === active.id;
                  const dot = agendaDotColor(r.color, i);
                  const hasHours = r.hours.length > 0;
                  return (
                    <TouchableOpacity
                      key={r.id}
                      style={[styles.sheetRow, selected && styles.sheetRowActive]}
                      onPress={() => {
                        props.onMobileResourceChange(r.id);
                        setMobilePickerOpen(false);
                      }}
                      activeOpacity={0.85}
                    >
                      <View style={[styles.dot, { backgroundColor: dot }]} />
                      <View style={{ flex: 1 }}>
                        <Text
                          style={[styles.sheetRowText, selected && styles.sheetRowTextActive]}
                          numberOfLines={1}
                        >
                          {r.displayName}
                        </Text>
                        <Text style={styles.sheetRowSub}>
                          {hasHours ? 'Con horario' : 'Sin horario'}
                        </Text>
                      </View>
                      {selected ? <Text style={styles.sheetCheck}>✓</Text> : null}
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
              <TouchableOpacity
                style={styles.sheetCancel}
                onPress={() => setMobilePickerOpen(false)}
                activeOpacity={0.85}
              >
                <Text style={styles.sheetCancelText}>Cancelar</Text>
              </TouchableOpacity>
            </Pressable>
          </Pressable>
        </Modal>
      </>
    );
  }

  const visible = resources.filter((r) => props.desktopSelectedIds.includes(r.id));

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      style={styles.bar}
      contentContainerStyle={styles.row}
    >
      <Text style={styles.label}>Viendo</Text>
      {visible.map((r) => {
        const idx = indexById.get(r.id) ?? 0;
        const dot = agendaDotColor(r.color, idx);
        return (
          <View key={r.id} style={styles.chip}>
            <View style={[styles.dot, { backgroundColor: dot }]} />
            <Text style={styles.chipText} numberOfLines={1}>
              {r.displayName}
            </Text>
          </View>
        );
      })}
      <TouchableOpacity
        style={[styles.chooseBtn, Platform.OS === 'web' && styles.webReset]}
        onPress={props.onPressChoose}
        activeOpacity={0.85}
      >
        <Text style={styles.chooseBtnText}>
          Elegir agendas ({props.desktopSelectedIds.length}/{resources.length})
        </Text>
      </TouchableOpacity>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  bar: {
    flexGrow: 0,
    maxHeight: 48,
    backgroundColor: agenda.surface2,
    borderBottomWidth: 1,
    borderBottomColor: agenda.border,
  },
  mobileBar: {
    maxHeight: 52,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 8,
    gap: 10,
  },
  row: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    gap: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  label: {
    fontSize: 11,
    fontWeight: '700',
    color: agenda.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    flexShrink: 0,
    fontFamily: agendaFonts.sans,
  },
  mobileCurrent: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  mobileName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  barSummary: {
    flexShrink: 0,
    fontSize: 11.5,
    fontWeight: '600',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  changeBtn: {
    flexShrink: 0,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: agenda.radius,
    borderWidth: 1,
    borderColor: agenda.brand,
    backgroundColor: agenda.chooseBg,
  },
  changeBtnText: {
    fontSize: 12,
    fontWeight: '700',
    color: agenda.chooseInk,
    fontFamily: agendaFonts.sans,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: agenda.radius,
    borderWidth: 1,
    borderColor: agenda.border,
    backgroundColor: agenda.surface,
    flexShrink: 0,
  },
  chipText: { fontSize: 12, fontWeight: '600', color: agenda.ink, fontFamily: agendaFonts.sans },
  dot: { width: 8, height: 8, borderRadius: 4 },
  chooseBtn: {
    flexShrink: 0,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: agenda.radius,
    borderWidth: 1,
    borderColor: agenda.brand,
    backgroundColor: agenda.chooseBg,
  },
  chooseBtnText: {
    fontSize: 12,
    fontWeight: '700',
    color: agenda.chooseInk,
    fontFamily: agendaFonts.sans,
  },
  sheetBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: agenda.surface,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingTop: 16,
    paddingBottom: 24,
    paddingHorizontal: 16,
    maxHeight: '70%',
  },
  sheetTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: agenda.ink,
    marginBottom: 12,
    fontFamily: agendaFonts.sans,
  },
  sheetList: { flexGrow: 0, flexShrink: 1, minHeight: 0, maxHeight: 360 },
  sheetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 14,
    paddingHorizontal: 10,
    borderRadius: agenda.radius,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: agenda.border,
  },
  sheetRowActive: { backgroundColor: agenda.chooseBg },
  sheetRowText: { fontSize: 15, fontWeight: '600', color: agenda.ink, fontFamily: agendaFonts.sans },
  sheetRowSub: { fontSize: 12, color: agenda.ink2, marginTop: 1, fontFamily: agendaFonts.sans },
  sheetRowTextActive: { color: agenda.chooseInk },
  sheetCheck: { fontSize: 14, fontWeight: '700', color: agenda.brand },
  sheetCancel: {
    flexShrink: 0,
    marginTop: 12,
    alignItems: 'center',
    paddingVertical: 12,
  },
  sheetCancelText: { fontSize: 14, fontWeight: '600', color: agenda.ink2, fontFamily: agendaFonts.sans },
  webReset: Platform.select({
    web: {
      boxShadow: 'none',
      outlineStyle: 'none',
    } as object,
    default: {},
  }),
});
